import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Navigation2 } from "lucide-react";

// Garde le nom du lieu/rue quand Mapbox ne renvoie que « code postal, ville ».
const withName = (name, rest) => {
  if (!rest) return name || "";
  if (!name || rest.toLowerCase().startsWith(name.toLowerCase())) return rest;
  return `${name}, ${rest}`;
};

/**
 * Minimal cartoon/street Mapbox address autocomplete.
 *
 * Props:
 *  - value / onChange  → controlled string input
 *  - onPick(place)     → optional callback with {name, lng, lat}
 *  - placeholder       → input placeholder
 *  - proximity         → "lng,lat" bias for suggestions (auto-detected
 *                        from geoloc when null)
 *  - icon              → left-side icon (defaults to MapPin)
 *  - label             → small uppercase label on top
 *  - testid            → data-testid for the input
 */
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const COUNTRIES = "FR,MC,BE,CH,IT,ES,LU,DE,GB,PT,NL";

// Persistent session token for Mapbox Search Box billing (one session
// covers a whole "type-then-click" cycle).
let __mbxSessionToken =
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
function refreshSearchSession() {
  __mbxSessionToken =
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AddressField({
  value,
  onChange,
  onPick,
  placeholder,
  proximity,
  icon,
  label,
  testid,
  autoGeoloc = false,
  onGeolocDone,
}) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [geoloading, setGeoloading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const justPickedRef = useRef(false);

  // Close on click outside
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  // Mapbox forward-geocoding on input change (debounced 300ms)
  useEffect(() => {
    if (!focused) return;
    if (justPickedRef.current) {
      // Skip the query that fires right after picking a suggestion —
      // otherwise we'd re-open the dropdown on the just-filled value.
      justPickedRef.current = false;
      return;
    }
    if (!MAPBOX_TOKEN) return;
    const q = (value || "").trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        // Mapbox Search Box v1 — much richer POI index than the legacy
        // Geocoding v5 (nightclubs, restaurants, shops, landmarks…).
        // The API is 2-step: /suggest returns lightweight results with
        // a `mapbox_id`, /retrieve is called at pick-time to get the
        // real lat/lng. We stash the id on each row so the pick handler
        // can hydrate it.
        const params = new URLSearchParams({
          q,
          access_token: MAPBOX_TOKEN,
          language: "fr",
          limit: "8",
          country: COUNTRIES.toLowerCase(),
          session_token: __mbxSessionToken,
          types: "poi,address,street,place,locality,neighborhood",
        });
        if (proximity) params.set("proximity", proximity);
        const r = await fetch(
          `https://api.mapbox.com/search/searchbox/v1/suggest?${params}`
        );
        if (!r.ok) throw new Error("mapbox " + r.status);
        const j = await r.json();
        const feats = (j.suggestions || []).map((f) => ({
          id: f.mapbox_id,
          mapbox_id: f.mapbox_id,
          name: f.name,
          short: f.name,
          full: withName(f.name, f.full_address || f.place_formatted),
          category: f.feature_type,
          // Suggest doesn't include coordinates — populated by /retrieve.
          lng: null,
          lat: null,
        }));
        setResults(feats);
        setOpen(feats.length > 0);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [value, proximity, focused]);

  // Auto-fill from geolocation on mount
  useEffect(() => {
    if (!autoGeoloc) return;
    if (!navigator.geolocation) return;
    if (value && value.trim()) return; // don't override
    setGeoloading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { longitude: lng, latitude: lat } = pos.coords;
        try {
          const params = new URLSearchParams({
            access_token: MAPBOX_TOKEN,
            language: "fr",
            limit: "1",
            types: "address,poi,place",
          });
          const r = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${params}`
          );
          if (r.ok) {
            const j = await r.json();
            const feat = (j.features || [])[0];
            if (feat?.place_name) {
              onChange({ target: { value: feat.place_name } });
              if (onPick) onPick({ name: feat.place_name, lng, lat });
            }
          }
        } catch (_e) { /* ignore geocode errors */ }
        if (onGeolocDone) onGeolocDone(`${lng},${lat}`);
        setGeoloading(false);
      },
      () => {
        setGeoloading(false);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePick = async (feat) => {
    justPickedRef.current = true;
    const displayName = feat.full || feat.name;
    onChange({ target: { value: displayName } });
    setResults([]);
    setOpen(false);
    // Blur to hide native keyboard & close autocomplete cleanly.
    if (inputRef.current) inputRef.current.blur();
    // Search Box suggestions carry a mapbox_id but no coordinates. Call
    // /retrieve to hydrate the pick with lat/lng before bubbling it up.
    let lat = feat.lat, lng = feat.lng;
    if (feat.mapbox_id && (lat == null || lng == null) && MAPBOX_TOKEN) {
      try {
        const params = new URLSearchParams({
          access_token: MAPBOX_TOKEN,
          session_token: __mbxSessionToken,
          language: "fr",
        });
        const r = await fetch(
          `https://api.mapbox.com/search/searchbox/v1/retrieve/${encodeURIComponent(feat.mapbox_id)}?${params}`
        );
        if (r.ok) {
          const j = await r.json();
          const geom = (j.features || [])[0]?.geometry?.coordinates || [];
          if (geom.length === 2) {
            lng = geom[0];
            lat = geom[1];
          }
        }
      } catch (_e) { /* ignore retrieve error, onPick will get null coords */ }
      // Rotate the session token after a successful pick — Mapbox bills
      // per session, not per request.
      refreshSearchSession();
    }
    if (onPick) onPick({ ...feat, name: displayName, lat, lng });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setGeoloading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { longitude: lng, latitude: lat } = pos.coords;
        try {
          const params = new URLSearchParams({
            access_token: MAPBOX_TOKEN,
            language: "fr",
            limit: "1",
            types: "address,poi,place",
          });
          const r = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${params}`
          );
          if (r.ok) {
            const j = await r.json();
            const feat = (j.features || [])[0];
            if (feat?.place_name) {
              onChange({ target: { value: feat.place_name } });
              if (onPick) onPick({ name: feat.place_name, lng, lat });
            }
          }
        } catch (_e) { /* ignore geocode errors */ }
        if (onGeolocDone) onGeolocDone(`${lng},${lat}`);
        setGeoloading(false);
      },
      () => setGeoloading(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && (
        <span className="mb-1 flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-foreground/70">
          {icon || <MapPin className="h-4 w-4 text-primary" />}
          {label}
        </span>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={onChange}
          onFocus={() => {
            setFocused(true);
            if (results.length > 0) setOpen(true);
          }}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={placeholder}
          data-testid={testid}
          autoComplete="off"
          className="w-full rounded-2xl bg-secondary/40 pl-4 pr-11 py-3 font-[Outfit] font-medium text-foreground placeholder:text-foreground/35 street-border-thin outline-none focus:bg-white focus:ring-4 focus:ring-primary/25"
          style={{ fontSize: "16px" }}
        />
        <button
          type="button"
          onClick={useMyLocation}
          disabled={geoloading}
          aria-label="Utiliser ma position"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground street-border-thin"
        >
          {geoloading || loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Navigation2 className="h-4 w-4" strokeWidth={2.5} />
          )}
        </button>
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-2 rounded-2xl bg-white p-1.5 street-border street-shadow max-h-[280px] overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                handlePick(r);
              }}
              className="flex w-full items-start gap-2.5 rounded-xl p-2.5 text-left hover:bg-primary/5 active:bg-primary/10"
            >
              <div className={
                "mt-0.5 grid h-8 w-8 flex-none place-items-center rounded-lg text-primary " +
                (r.category === "poi" ? "bg-yellow-100 text-yellow-800" : "bg-primary/10")
              }>
                <MapPin className="h-4 w-4" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-[Outfit] text-[14px] font-bold leading-tight text-foreground">
                  {r.short}
                </div>
                <div className="truncate text-[12px] text-foreground/60 font-medium">
                  {(r.full || "").replace(new RegExp("^" + r.short + ",\\s*"), "") || r.category || ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
